import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { FolderOpen, Plus, FileText, Calendar, User, X, Settings, Trash2, Share2, UserPlus } from 'lucide-react'
import { projectsApi, Project, ProjectShare } from '../services/api'
import { useProject } from '../contexts/ProjectContext'
import ConfirmDialog from '../components/ConfirmDialog'

export default function ProjectsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectDescription, setNewProjectDescription] = useState('')
  const [shareEmail, setShareEmail] = useState('')
  const [shareError, setShareError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setCurrentProject } = useProject()

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then(r => r.data),
  })

  // Shares query — only when edit modal is open and user is owner
  const { data: shares = [] } = useQuery({
    queryKey: ['project-shares', editingProject?.id],
    queryFn: () => projectsApi.getShares(editingProject!.id).then(r => r.data),
    enabled: isEditModalOpen && !!editingProject && editingProject.isOwner !== false,
  })

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      projectsApi.create(data).then(r => r.data),
    onSuccess: (newProject) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      handleCloseModal()
      // Select the new project and navigate to its detail page
      setCurrentProject(newProject)
      navigate(`/projects/${newProject.id}`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string } }) =>
      projectsApi.update(id, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      handleCloseEditModal()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      projectsApi.delete(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setDeleteTarget(null)
      setDeleteError(null)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error || 'Nie udalo sie usunac projektu.'
      setDeleteError(msg)
    },
  })

  const addShareMutation = useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      projectsApi.addShare(id, email).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-shares', editingProject?.id] })
      setShareEmail('')
      setShareError('')
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error || 'Nie udalo sie udostepnic projektu'
      setShareError(msg)
    },
  })

  const removeShareMutation = useMutation({
    mutationFn: ({ projectId, shareId }: { projectId: string; shareId: string }) =>
      projectsApi.removeShare(projectId, shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-shares', editingProject?.id] })
    },
  })

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setNewProjectName('')
    setNewProjectDescription('')
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    setEditingProject(null)
    setNewProjectName('')
    setNewProjectDescription('')
    setShareEmail('')
    setShareError('')
  }

  const handleCreateProject = (e: React.FormEvent) => {
    e.preventDefault()
    if (newProjectName.trim()) {
      createMutation.mutate({
        name: newProjectName,
        description: newProjectDescription || undefined,
      })
    }
  }

  const handleEditProject = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingProject && newProjectName.trim()) {
      updateMutation.mutate({
        id: editingProject.id,
        data: {
          name: newProjectName,
          description: newProjectDescription || undefined,
        },
      })
    }
  }

  const handleOpenEdit = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingProject(project)
    setNewProjectName(project.name)
    setNewProjectDescription(project.description || '')
    setIsEditModalOpen(true)
  }

  const handleDeleteProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation()
    setDeleteError(null)
    setDeleteTarget(project)
  }

  const confirmDelete = () => {
    if (!deleteTarget) return
    const hasDocuments = deleteTarget.documentsCount > 0
    deleteMutation.mutate({ id: deleteTarget.id, force: hasDocuments || undefined })
  }

  const cancelDelete = () => {
    setDeleteTarget(null)
    setDeleteError(null)
  }

  const handleSelectProject = (project: Project) => {
    setCurrentProject(project)
    navigate(`/projects/${project.id}`)
  }

  const handleAddShare = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingProject && shareEmail.trim()) {
      setShareError('')
      addShareMutation.mutate({ id: editingProject.id, email: shareEmail.trim() })
    }
  }

  const handleRemoveShare = (share: ProjectShare) => {
    if (editingProject) {
      removeShareMutation.mutate({ projectId: editingProject.id, shareId: share.id })
    }
  }

  const isOwner = (project: Project) => project.isOwner !== false

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projekty</h1>
          <p className="text-gray-500">Zarzadzaj projektami i grupuj dokumenty</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Nowy projekt
        </button>
      </div>

      {/* Create Project Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-gray-600 bg-opacity-50"
            onClick={handleCloseModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Nowy projekt</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa projektu *
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="np. Sprawa cywilna ABC"
                  className="input w-full"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opis (opcjonalnie)
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="Krotki opis projektu..."
                  className="input w-full h-24 resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !newProjectName.trim()}
                  className="btn-primary"
                >
                  {createMutation.isPending ? 'Tworzenie...' : 'Utworz projekt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {isEditModalOpen && editingProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-gray-600 bg-opacity-50"
            onClick={handleCloseEditModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Edytuj projekt</h2>
              <button
                onClick={handleCloseEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa projektu *
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="np. Sprawa cywilna ABC"
                  className="input w-full"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opis (opcjonalnie)
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="Krotki opis projektu..."
                  className="input w-full h-24 resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending || !newProjectName.trim()}
                  className="btn-primary"
                >
                  {updateMutation.isPending ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </button>
              </div>
            </form>

            {/* Sharing section — only for owned projects */}
            {isOwner(editingProject) && (
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center gap-2 mb-4">
                  <Share2 className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-700">Udostepnianie</h3>
                </div>

                {/* Add share form */}
                <form onSubmit={handleAddShare} className="flex gap-2 mb-4">
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => {
                      setShareEmail(e.target.value)
                      setShareError('')
                    }}
                    placeholder="Adres email uzytkownika"
                    className="input flex-1"
                  />
                  <button
                    type="submit"
                    disabled={addShareMutation.isPending || !shareEmail.trim()}
                    className="btn-primary whitespace-nowrap"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    {addShareMutation.isPending ? '...' : 'Udostepnij'}
                  </button>
                </form>

                {shareError && (
                  <p className="text-sm text-red-600 mb-3">{shareError}</p>
                )}

                {/* Shares list */}
                {shares.length > 0 ? (
                  <div className="space-y-2">
                    {shares.map((share) => (
                      <div
                        key={share.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {share.userName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {share.userEmail}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveShare(share)}
                          disabled={removeShareMutation.isPending}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                          title="Cofnij udostepnienie"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    Projekt nie jest jeszcze nikomu udostepniony
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Projects grid */}
      {isLoading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        </div>
      ) : projects.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              onClick={() => handleSelectProject(project)}
              className="card p-4 cursor-pointer hover:shadow-lg transition-shadow relative group"
            >
              {/* Actions — only for owned projects */}
              {isOwner(project) && (
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => handleOpenEdit(project, e)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Edytuj"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => handleDeleteProject(project, e)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Usun"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Shared badge */}
              {project.isShared && (
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    <Share2 className="w-3 h-3" />
                    Udostepniony
                  </span>
                </div>
              )}

              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${project.isShared ? 'bg-purple-100' : 'bg-blue-100'}`}>
                  <FolderOpen className={`w-6 h-6 ${project.isShared ? 'text-purple-600' : 'text-blue-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {project.name}
                  </h3>
                  {project.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                      {project.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  <span>{project.documentsCount} dokumentow</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(project.updatedAt).toLocaleDateString('pl-PL')}</span>
                </div>
              </div>

              {project.isShared && project.sharedByName ? (
                <div className="mt-2 flex items-center gap-1 text-xs text-purple-500">
                  <Share2 className="w-3 h-3" />
                  <span>Udostepniony przez {project.sharedByName}</span>
                </div>
              ) : project.createdByName ? (
                <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                  <User className="w-3 h-3" />
                  <span>{project.createdByName}</span>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-8 text-center text-gray-500">
          <FolderOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p className="mb-4">Brak projektow</p>
          <button onClick={() => setIsModalOpen(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Utworz pierwszy projekt
          </button>
        </div>
      )}

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Usun projekt"
        message={
          deleteTarget && deleteTarget.documentsCount > 0
            ? `Projekt "${deleteTarget.name}" zawiera ${deleteTarget.documentsCount} dokumentow. Usuniecie projektu spowoduje rowniez usuniecie wszystkich dokumentow. Kontynuowac?`
            : `Czy na pewno chcesz usunac projekt "${deleteTarget?.name}"?`
        }
        confirmLabel="Usun"
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  )
}
